import { type FunctionComponent, useEffect, useState } from 'react';
import { configureRoute } from '../../../../utils/routes/configure-route';
import { getQueryParamValue } from '../../../../utils/string/url.utils';
import { TextButton } from '../../../components/buttons';
import { LinkButton } from '../../../components/buttons/link-button/link-button.component';
import { AlertNotification } from '../../../components/common-components/alert-notification/alert-notification.component';
import { Drawer } from '../../../components/common-components/drawer/drawer.component';
import { PageContainer } from '../../../components/common-components/page/page-container/page-container.component';
import { GoogleMap } from '../../../components/google-maps/google-maps.component';
import { PlaceholderImage } from '../../../components/images/placeholder-image/placeholder-image.component';
import { FindNearbyLocations } from '../../../components/showroom-components/find-nearby-locations/find-nearby-locations.component';
import {
	SearchResultCard,
	type SelectedShowroomDetails
} from '../../../components/showroom-components/location-search-result-card/location-search-result-card.component';
import { ShowroomAppointmentBookingModal } from '../../../components/showroom-components/showroom-appointment-booking-modal/showroom-appointment-booking-modal.component';
import { SHOWROOM_DIRECTORY_LINK, SHOWROOM_SEARCH_LINK } from '../../../constants/links';
import {
	createInfoBoxString,
	getHumanReadableLocationType,
	getSupportedLocationType,
	makeShowroomLocationLink
} from '../../../helpers/showroom/showroom.helper';
import { useTrackPageView } from '../../../hooks/analytics/analytics.hooks';
import { useCustomer } from '../../../hooks/apollo/customer/customer.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { useRouterLocation } from '../../../hooks/routing/location.hook';
import { useShowroomLocationData } from '../../../hooks/showroom/showroom-locations/showroom-locations.hooks';
import { type LocationMarker } from '../../../types/google-maps.types';
import { isChromatic } from '../../../utils/dom/chromatic.utils';

const SkeletonSearchCard: FunctionComponent = () => (
	<SearchResultCard
		// arbitrary prop values (except for searchResultsLoading), just a loading state component
		locationType={'APPLIANCE'}
		address={{ addressLine1: '', addressLine2: '', city: '', state: 'CA', zipCode: '', latitude: 0, longitude: 0, phoneNumber: '' }}
		branchPageUrl=""
		searchResultsLoading={true}
		isLastLocation={false}
	/>
);

export const ShowroomSearchResults: FunctionComponent = () => {
	const pageName = 'landing_pages:showroom_search_landing';
	const showroomSearchDataLayer = {
		page: pageName,
		isShowroom: true
	};
	useTrackPageView({ pageName }, showroomSearchDataLayer);

	const navigate = useNavigation();
	const { search } = useRouterLocation();
	const searchTermParam = getQueryParamValue('searchTerm', search);
	const { data: customerData } = useCustomer();
	const customerLocation = customerData?.customer.location;
	const userZipcode = customerLocation?.zipCode;

	const [searchTerm, setSearchTerm] = useState<string>(searchTermParam || userZipcode || '');

	// Update searchTerm when it changes in the URL.
	useEffect(() => {
		setSearchTerm(searchTermParam || userZipcode || '');
	}, [setSearchTerm, searchTermParam]);

	const { sortedShowroomLocations, unsortedLocations, loading } = useShowroomLocationData(searchTerm);
	const countOfLocations = sortedShowroomLocations?.length || unsortedLocations?.length || 0;
	const locationsToRender = !loading && sortedShowroomLocations ? sortedShowroomLocations : unsortedLocations;

	const markers: LocationMarker[] | undefined = locationsToRender?.map((store) => {
		const { address, branchPageUrl, locationTypes } = store;
		const { latitude, longitude } = address;
		const supportedLocationType = getSupportedLocationType(locationTypes);
		const locationName = supportedLocationType && getHumanReadableLocationType(supportedLocationType);
		return {
			latitude,
			longitude,
			info: createInfoBoxString(locationName, makeShowroomLocationLink(branchPageUrl), address)
		};
	});

	const loadingText = `Searching for locations near ${searchTerm}...`;
	const nearestShowroomsText = 'Your Nearest Showrooms';
	const errorText = 'No Results Found.';

	const breadcrumbs = [
		{
			name: 'Showrooms',
			url: '/showroom'
		},
		{
			name: `Locations Near ${searchTerm}`
		}
	];

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const closeAction = (): void => {
		setIsDrawerOpen(false);
	};
	const openDrawer = (): void => {
		setIsDrawerOpen(true);
	};
	const onZipInput = (input: string): void => {
		setIsDrawerOpen(false);
		if (searchTerm !== input) {
			navigate('?searchTerm=' + input);
		}
	};

	const [isOnClient, setIsOnClient] = useState<boolean>(false);
	useEffect(() => {
		setIsOnClient(true);
	}, []);

	const [selectedShowroom, setSelectedShowroom] = useState<SelectedShowroomDetails>();
	const onCancelFlow = (): void => {
		setSelectedShowroom(undefined);
	};
	const onFinishFlow = (): void => {
		setSelectedShowroom(undefined);
	};

	useEffect(() => {
		if ((!searchTerm || countOfLocations === 0) && !loading) {
			setIsDrawerOpen(true);
		}
	}, [loading]);

	return isOnClient ? (
		<>
			{selectedShowroom && !isChromatic() && (
				<div>
					<ShowroomAppointmentBookingModal
						branchPageUrl={selectedShowroom.branchPageUrl}
						locationTitle={selectedShowroom.locationType}
						locationCity={selectedShowroom.address.city}
						locationState={selectedShowroom.address.state}
						locationZip={selectedShowroom.address.zipCode}
						onCancelFlow={onCancelFlow}
						onFinishFlow={onFinishFlow}
					/>
				</div>
			)}

			{isDrawerOpen && (
				<Drawer onClose={closeAction} ariaLabel="Find your local showroom" direction="left" size="full">
					<div className="font-medium f6 f5-l lh-copy">Find your local showroom to book an appointment.</div>
					{countOfLocations === 0 && (
						<div className="mv5">
							<AlertNotification size="SMALL" alertType="danger" showCloseButton={false}>
								There is an error with the search term you entered, please try again.
							</AlertNotification>
						</div>
					)}
					<FindNearbyLocations onSuccessSubmit={onZipInput} userZipcode={userZipcode} />
				</Drawer>
			)}
			<PageContainer
				pageTitle="Search Results - Ferguson Home Showrooms Near You"
				canonicalURL={SHOWROOM_SEARCH_LINK}
				metaDescription="Search Results for Ferguson Home showrooms in your area. Our Bath, Kitchen &amp; Lighting Gallery Showrooms offer premium products &amp; assistance for your next renovation or build."
				breadcrumbs={breadcrumbs}>
				<>
					<h2 className="f2 f1-ns lh-title fw5 theme-grey-darker mv0 font-medium">
						{loading ? loadingText : countOfLocations > 0 ? nearestShowroomsText : errorText}
					</h2>
					<div className="flex flex-column flex-row-ns mt2 items-start items-center-ns ma0 mb2">
						<p className="f5 f4-ns mv0 mr3-ns theme-grey-darker lh-copy">Showing results for {searchTerm}</p>
						<TextButton
							testId="change-location"
							className="font-medium f5 f4-ns mt1 mt0-ns"
							underlineHover={true}
							onClick={openDrawer}>
							Change Location
						</TextButton>
					</div>

					{loading ? (
						<div className="mt4 mt6-ns flex">
							<div className="w-50-ns pr5-ns pr7-l">
								{Array.from({ length: 3 }, (_, index) => (
									<SkeletonSearchCard key={index} />
								))}
							</div>
							<div className="dn db-ns w-50-ns">
								<PlaceholderImage bypassLazyLoad={true} />
							</div>
						</div>
					) : (
						<div className="mt4 mt6-ns flex">
							<div className="w-50-ns pr5-ns pr7-l">
								{locationsToRender?.map((location, index) => {
									const locationType = getSupportedLocationType(location.locationTypes);
									return (
										locationType && (
											<SearchResultCard
												key={location.id}
												isLastLocation={locationsToRender?.length === index + 1}
												address={location.address}
												locationType={locationType}
												branchPageUrl={location.branchPageUrl}
											/>
										)
									);
								})}
							</div>

							<div className="dn db-ns w-50-ns sticky top-0 self-start">
								{isChromatic() ? <PlaceholderImage /> : <GoogleMap markers={markers} searchAddress={searchTerm} />}
							</div>
						</div>
					)}
					<div className="w-100 bb b--theme-grey-light mb3 mt6"></div>
					<LinkButton buttonStyle="SECONDARY" url={SHOWROOM_DIRECTORY_LINK}>
						View Complete Location List
					</LinkButton>
				</>
			</PageContainer>
		</>
	) : null;
};

export const RoutedShowroomSearch = configureRoute({ path: SHOWROOM_SEARCH_LINK }, ShowroomSearchResults);
